import { useState } from "react";
import { openExternalUrl } from "@/lib/open-url";
import { Play, X, ArrowSquareOut } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { cn } from "@/lib/utils";
import { getVideoEmbedUrl, getVideoThumbnailUrl } from "@/lib/url-utils";

interface VideoLinkPreviewProps {
  preview: {
    url: string;
    title?: string | null;
    description?: string | null;
    image_url?: string | null;
    favicon_url?: string | null;
    site_name?: string | null;
  };
}

export function VideoLinkPreview({ preview }: VideoLinkPreviewProps) {
  const { url, title, image_url, favicon_url, site_name } = preview;
  const [isPlaying, setIsPlaying] = useState(false);

  const embedUrl = getVideoEmbedUrl(url);
  if (!embedUrl) return null;

  const thumbnailUrl = image_url || getVideoThumbnailUrl(url);

  const hostname = (() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  })();

  const handleOpenExternal = (e: React.MouseEvent) => {
    e.stopPropagation();
    void openExternalUrl(url);
  };

  return (
    <div
      className={cn(
        "mt-2 max-w-md rounded-lg overflow-hidden",
        "bg-secondary/50",
      )}
    >
      {/* Thumbnail / Player area */}
      <div className="relative aspect-video w-full bg-accent">
        {isPlaying ? (
          <>
            <iframe
              src={embedUrl}
              className="h-full w-full"
              title={title || "Embedded video"}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
            <ToolbarIconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsPlaying(false);
              }}
              className="absolute right-2 top-2 z-10 bg-overlay/60 hover:bg-overlay/80"
              aria-label="Close video"
            >
              <X size={16} className="text-foreground" weight="Bold" />
            </ToolbarIconButton>
          </>
        ) : (
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setIsPlaying(true);
            }}
            className="group relative h-full w-full cursor-pointer rounded-none p-0 hover:bg-transparent"
            aria-label="Play video"
          >
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className={cn(
                  "flex h-14 w-14 items-center justify-center rounded-full",
                  "bg-overlay/60 transition-colors group-hover:bg-overlay/80",
                )}
              >
                <Play
                  size={28}
                  weight="Bold"
                  className="ml-0.5 text-foreground"
                />
              </div>
            </div>
          </Button>
        )}
      </div>

      {/* Info bar */}
      <div className="px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {favicon_url && (
            <img
              src={favicon_url}
              alt=""
              className="h-3.5 w-3.5 shrink-0 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <span className="truncate">{site_name || hostname}</span>
          <ToolbarIconButton
            onClick={handleOpenExternal}
            className="ml-auto h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Open in browser"
          >
            <ArrowSquareOut size={14} />
          </ToolbarIconButton>
        </div>
        {title && (
          <p className="mt-0.5 truncate text-sm font-medium text-foreground">
            {title}
          </p>
        )}
      </div>
    </div>
  );
}
